// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R

import React, { useCallback, useMemo } from 'react';
import { NumberWheelProps, WheelPickerOption } from 'util/Types';
import { WheelPicker } from 'components/atoms/WheelPicker';
import styles from './NumberWheel.module.scss';
import { mergeClasses } from 'util/commons';

const NumberWheel: React.FC<NumberWheelProps> = ({
  value,
  integerMin = 0,
  integerMax = 99,
  decimalMin = 0,
  decimalMax,
  decimalPlaces = 1,
  showDecimal = true,
  onChange,
  separator,
  classNames,
  wheelConfigs,
}) => {
  const actualDecimalMax = decimalMax ?? Math.pow(10, decimalPlaces) - 1;

  // Parse current value
  const currentValue = value ?? 0;
  const integerPart = Math.floor(currentValue);
  const decimalPart = Math.round(
    (currentValue % 1) * Math.pow(10, decimalPlaces)
  );

  // Generate options
  const integerOptions = useMemo(() => {
    const options: WheelPickerOption[] = [];
    for (let i = integerMin; i <= integerMax; i++) {
      options.push({
        value: i.toString(),
        label: i.toString(),
      });
    }
    return options;
  }, [integerMin, integerMax]);

  const decimalOptions = useMemo(() => {
    const options: WheelPickerOption[] = [];
    for (let i = decimalMin; i <= actualDecimalMax; i++) {
      options.push({
        value: i.toString(),
        label: i.toString().padStart(decimalPlaces, '0'),
      });
    }
    return options;
  }, [decimalMin, actualDecimalMax, decimalPlaces]);

  // Handle value changes
  const handleIntegerChange = useCallback(
    (integerValue: string) => {
      if (onChange) {
        const newInteger = parseInt(integerValue, 10);
        const newDecimal = decimalPart / Math.pow(10, decimalPlaces);
        onChange(newInteger + newDecimal);
      }
    },
    [onChange, decimalPart, decimalPlaces]
  );

  const handleDecimalChange = useCallback(
    (decimalValue: string) => {
      if (onChange) {
        const newDecimal =
          parseInt(decimalValue, 10) / Math.pow(10, decimalPlaces);
        onChange(integerPart + newDecimal);
      }
    },
    [onChange, integerPart, decimalPlaces]
  );

  return (
    <div className={mergeClasses(styles.numberWheel, classNames?.wrapper)}>
      <div
        className={mergeClasses(styles.wheelContainer, classNames?.container)}
      >
        <WheelPicker
          options={integerOptions}
          value={integerPart.toString()}
          onValueChange={handleIntegerChange}
          className={mergeClasses(
            styles.integerWheel,
            styles.roundedLeftHighlight,
            classNames?.integerWheel
          )}
          {...wheelConfigs}
        />

        {showDecimal && (
          <>
            {separator && (
              <span
                className={mergeClasses(
                  styles.separator,
                  classNames?.separator
                )}
              >
                {separator}
              </span>
            )}
            <WheelPicker
              options={decimalOptions}
              value={decimalPart.toString()}
              onValueChange={handleDecimalChange}
              className={mergeClasses(
                styles.decimalWheel,
                styles.roundedRightHighlight,
                classNames?.decimalWheel
              )}
              {...wheelConfigs}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default NumberWheel;
